import { useEffect, useState } from "react";
import Marquee from "react-fast-marquee";
import { Star } from "lucide-react";
import { api } from "../api";
import { TypeOnView } from "./TypeOnView";

const Stars = () => (
  <div className="flex gap-0.5">
    {[...Array(5)].map((_, i) => (
      <Star key={i} size={15} className="fill-amber-400 text-amber-400" />
    ))}
  </div>
);

const AVATAR_COLORS = ["#EA4335", "#4285F4", "#34A853", "#9C27B0", "#009688", "#FF7043"];

const avatarColor = (name = "G") => AVATAR_COLORS[(name.charCodeAt(0) || 0) % AVATAR_COLORS.length];

const GoogleG = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" aria-label="Google">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
    <path fill="#FBBC05" d="M5.84 14.1c-.22-.66-.35-1.36-.35-2.1s.13-1.44.35-2.1V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l3.66-2.84z" />
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z" />
  </svg>
);

const ReviewCard = ({ review, profileUrl }) => (
  <a
    href={review.googleMapsUri || profileUrl}
    target="_blank"
    rel="noreferrer"
    className="review-marquee-card block mx-3 bg-white rounded-2xl p-6 shadow-xl hover:shadow-2xl hover:-translate-y-1 transition-[box-shadow,transform] cursor-pointer"
    data-testid="review-card"
  >
    <div className="flex items-start justify-between mb-3">
      <div className="flex items-center gap-3">
        {review.photo ? (
          <img src={review.photo} alt={review.author} className="w-10 h-10 rounded-full object-cover shrink-0" />
        ) : (
          <span
            className="w-10 h-10 rounded-full flex items-center justify-center text-white text-base font-semibold shrink-0"
            style={{ backgroundColor: avatarColor(review.author) }}
          >
            {(review.author || "G")[0]}
          </span>
        )}
        <div>
          <p className="text-[15px] font-medium text-slate-900 leading-tight">{review.author || "Google Reviewer"}</p>
          <p className="text-xs text-slate-500 mt-0.5">Google review</p>
        </div>
      </div>
      <GoogleG />
    </div>
    <div className="flex items-center gap-2 mb-2.5">
      <Stars />
      <span className="text-xs text-slate-500">{review.relativeTime || ""}</span>
    </div>
    <p className="text-sm text-slate-700 leading-relaxed line-clamp-5 min-h-[105px]">{review.text}</p>
  </a>
);

export const ReviewsMarquee = () => {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/reviews").then((r) => setData(r.data)).catch(() => setData({ reviews: [] }));
  }, []);

  if (!data) {
    return (
      <section className="bg-obsidian py-20" data-testid="reviews-section">
        <div className="max-w-7xl mx-auto px-6 animate-pulse space-y-4">
          <div className="h-8 w-72 bg-white/10 rounded" />
          <div className="h-32 bg-white/5 rounded-xl" />
        </div>
      </section>
    );
  }

  let reviews = data.reviews || [];
  if (reviews.length > 0 && reviews.length < 6) reviews = [...reviews, ...reviews].slice(0, 8);
  const row1 = reviews.filter((_, i) => i % 2 === 0);
  const row2 = reviews.filter((_, i) => i % 2 === 1);

  return (
    <section className="bg-obsidian py-20 overflow-hidden border-y border-white/10" data-testid="reviews-section">
      <div className="max-w-7xl mx-auto px-6 mb-10 flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <p className="text-brand font-heading font-bold tracking-[0.25em] text-xs uppercase mb-3">Real Google Reviews</p>
          <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-white">
            <TypeOnView text="Trusted by Dog Owners Across Calgary & Surrounding Areas!" />
          </h2>
        </div>
        {data.rating && (
          <a href={data.googleMapsUri} target="_blank" rel="noreferrer" data-testid="reviews-google-link"
             className="flex items-center gap-3 bg-white rounded-full px-5 py-3 hover:shadow-xl transition-shadow shrink-0">
            <Stars />
            <span className="text-slate-900 font-heading font-bold text-lg">{data.rating.toFixed(1)}</span>
            <span className="text-slate-500 text-sm">({data.userRatingCount} reviews)</span>
          </a>
        )}
      </div>
      {reviews.length === 0 ? (
        <div className="max-w-7xl mx-auto px-6">
          <a href="https://share.google/J7tijMnPGNGThlRK6" target="_blank" rel="noreferrer" data-testid="reviews-fallback-link"
             className="inline-flex items-center gap-3 bg-[#121212] border border-white/10 rounded-xl px-6 py-5 hover:border-brand/50 transition-colors">
            <Stars />
            <span className="text-white/80 text-sm">Read our 5-star reviews on Google</span>
          </a>
        </div>
      ) : (
        <div className="space-y-5" data-testid="reviews-marquees">
          <Marquee speed={35} gradient gradientColor="#0A0A0A" gradientWidth={80} pauseOnHover>
            {row1.map((r, i) => <ReviewCard key={`a-${i}`} review={r} profileUrl={data.googleMapsUri} />)}
          </Marquee>
          <Marquee speed={35} direction="right" gradient gradientColor="#0A0A0A" gradientWidth={80} pauseOnHover>
            {(row2.length ? row2 : row1).map((r, i) => <ReviewCard key={`b-${i}`} review={r} profileUrl={data.googleMapsUri} />)}
          </Marquee>
        </div>
      )}
    </section>
  );
};
